import type { OnboardingEmailTemplate } from '../OnboardingEmailModal.types';
import { documentItem, optional, questionItem } from './templateItems';

const ENGAGEMENT_DETAILS = 'Engagement Details';
const COMPANY_DOCUMENTS = 'Company Documents/Information';
const VIU = 'Value in Use ("VIU") - Financial Projections';
const FVLCOD = 'Fair Value Less Cost of Disposal ("FVLCOD")';
const TRANSACTION_STRUCTURE = 'Hypothetical Transaction, Cost of Debt & Shares Outstanding';

// Based on the "GW Impairment Request" tab of the "IAS 36 - Goodwill and Intangible Asset Impairment"
// workbook. VIU and FVLCOD start unchecked; the lead picks the standard of value that applies.
export const IAS_36_GOODWILL_IMPAIRMENT_TEMPLATE: OnboardingEmailTemplate = {
  id: 'ias-36-goodwill-impairment',
  label: 'Goodwill & Indefinite-Lived Impairment (IAS 36)',
  emailSubject: 'Information request for your IAS 36 goodwill impairment analysis',
  valuationTypes: ['Impairment (IAS 36)'],
  requestValuationType: 'ias_36_goodwill_impairment',
  introduction: `Hello {{CONTACT_FIRST_NAME}},

Thank you for partnering with us. We are ready to move forward with the IAS 36 goodwill and indefinite-lived asset impairment testing.

To help us build an accurate model and keep things moving efficiently, please reply to this email with your responses to the questions below and upload the requested documents to the portal. Where available, Excel versions are strongly preferred to ensure precision in our analysis.`,
  documents: [
    questionItem(ENGAGEMENT_DETAILS, 'Company name', 'Company Name'),
    questionItem(ENGAGEMENT_DETAILS, 'CGUs', 'Number of Cash Generating Unit(s) ("CGUs") and the name of each CGU', {
      description:
        'please confirm the CGUs have been appropriately defined (multiple CGUs vs. one CGU), which should be discussed with your auditor',
    }),
    questionItem(
      ENGAGEMENT_DETAILS,
      'International CGUs',
      'Name(s) of the CGU(s) that are internationally based and the country/(ies) where each is based'
    ),
    questionItem(ENGAGEMENT_DETAILS, 'Measurement date', 'Measurement Date'),
    questionItem(ENGAGEMENT_DETAILS, 'Auditor', 'Auditor'),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Standard of value (VIU or FVLCOD)',
      'Standard of Value - Should we use value in use ("VIU") or fair value less cost of disposal ("FVLCOD") to calculate the recoverable amount?',
      {
        description:
          'if the selected standard results in an indication below the carrying amount, we will discuss with you before preparing an analysis under the second standard',
      }
    ),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Triggering event',
      'Triggering Event - Please describe any issues that could result in goodwill impairment'
    ),
    questionItem(
      ENGAGEMENT_DETAILS,
      'No plans to sell/dispose?',
      'Confirm Management has no immediate plans to sell or dispose of the CGU as of the Measurement Date'
    ),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Book value as proxy for fair value?',
      'Confirm book value is a reasonable proxy for fair value for all other balance sheet assets and liabilities of the CGU'
    ),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Indefinite-lived intangibles?',
      'Do any CGUs have indefinite-lived intangible assets on their balance sheet that require impairment testing as of the Measurement Date?',
      { description: 'if so, we will have follow-up questions in a separate data request' }
    ),
    questionItem(
      ENGAGEMENT_DETAILS,
      'Fixed assets tax / useful life',
      'Tax life / remaining useful life on existing fixed assets and new assets for the CGU as of the Measurement Date'
    ),
    questionItem(ENGAGEMENT_DETAILS, 'Carrying amount', 'Carrying amount for each CGU as of the Measurement Date', {
      description: 'as applicable',
    }),

    documentItem(
      COMPANY_DOCUMENTS,
      'Business plan / investment presentation',
      'Business plan or investment presentation for each CGU as of the Measurement Date',
      { description: 'if available' }
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Public comparables',
      'A list of public competitors and public comparable firms for each CGU',
      { description: 'if available' }
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Financials - Historical, last 3 years',
      'Historical annual income statements, balance sheets, and cash flow statements for each CGU',
      {
        description:
          'for three previous fiscal years if available; preferably in Excel. If not available by CGU, at a minimum we need the Measurement Date balance sheet, LTM income statement, and five-year projections by CGU',
      }
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Financials - LTM',
      'Last twelve months ("LTM") income statement, balance sheet, and cash flow statement for each CGU',
      {
        description:
          'as of the Measurement Date, with all amounts allocated from Corporate (both income statement and balance sheet); preferably in Excel',
      }
    ),
    questionItem(
      COMPANY_DOCUMENTS,
      'Balance sheet items not assumed',
      'In a hypothetical sale of the CGU, would any balance sheet items not be assumed by a willing buyer?',
      { description: 'such as intercompany accounts or related party loans, as applicable' }
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Balance sheet with goodwill & intangibles',
      'Balance sheet as of the Measurement Date for each CGU with goodwill and intangible asset(s) detailed by type'
    ),
    documentItem(
      COMPANY_DOCUMENTS,
      'Actuals vs. budget',
      'Comparison of income statement actuals versus budget for the current fiscal year end ("FYE") for each CGU',
      { description: 'if available; please briefly explain any major variances' }
    ),

    documentItem(VIU, 'VIU projections', 'VIU - Controlling projections for the next five years for each CGU', {
      ...optional,
      description:
        'entity-specific basis reflecting the current condition of the asset and excluding uncommitted enhancements; detail COGS, SG&A (stock-based compensation may not apply under VIU, check with auditor), R&D, depreciation, amortization, and capital expenditures; if available, base, optimistic, and pessimistic cases with probabilities; preferably in Excel',
    }),
    questionItem(
      VIU,
      'Maintenance capex %',
      'VIU - Percentage of capital expenditures related to maintenance efforts over the projection period',
      optional
    ),
    questionItem(
      VIU,
      'Strategy & assumptions',
      'VIU - Assumptions behind the projections and the market or business strategy for the CGU products/services',
      optional
    ),

    documentItem(
      FVLCOD,
      'FVLCOD projections',
      'FVLCOD - Controlling projections for the next five years (market participant basis)',
      {
        ...optional,
        description:
          'detail COGS, SG&A, R&D, depreciation, amortization, and capital expenditures, including only costs a market participant would assume; if available, base, optimistic, and pessimistic cases with probabilities; preferably in Excel',
      }
    ),
    questionItem(
      FVLCOD,
      'Costs of disposal',
      'FVLCOD - Costs that would be incurred in a hypothetical sale of the CGU',
      {
        ...optional,
        description:
          'assuming the sale takes place when the CGU reaches a normalized level of growth; e.g., legal costs or other direct incremental costs. Please advise the amount and what these costs relate to',
      }
    ),

    questionItem(
      TRANSACTION_STRUCTURE,
      'Stock or asset deal?',
      'Was the initial transaction structured as a stock deal or an asset deal for tax purposes?',
      { description: 'and can we assume another market participant would structure the transaction the same way?' }
    ),
    documentItem(
      TRANSACTION_STRUCTURE,
      'Projected tax amortization (stock deal)',
      'Projected tax amortization for each definite-lived intangible asset subject to amortization within the CGU',
      {
        description: 'for each fiscal year until the end of the remaining useful life; only applicable in a stock deal',
      }
    ),
    questionItem(
      TRANSACTION_STRUCTURE,
      'NOL balance (stock deal)',
      'Outstanding federal net operating loss (NOL) balance for each CGU that would transfer to a hypothetical buyer',
      { description: 'for US CGUs, pre- and post-2018 NOL balances; only applicable in a stock deal' }
    ),
    questionItem(
      TRANSACTION_STRUCTURE,
      'Shares outstanding (public)',
      'Shares outstanding as of the Measurement Date',
      {
        description: 'public companies only',
      }
    ),
    questionItem(
      TRANSACTION_STRUCTURE,
      'Cost of debt',
      'Cost of debt on interest-bearing debt as of the Measurement Date for each CGU',
      { description: 'as applicable' }
    ),
  ],
};
